<script setup lang="ts" generic="TOption extends ISuggestionPanelOption">
import { getSuggestionOptionId, type ISuggestionPanelOption } from '@/core/elements/suggestionPanel.utils';
import { maintainScrollVisibility } from '@/utils/menu.utils';
import { nextTick, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  select: [option: TOption];
  'update:activeIndex': [index: number];
}>();

const props = withDefaults(
  defineProps<{
    listboxId: string;
    options: readonly TOption[];
    activeIndex: number;
    dockToBottom?: boolean;
  }>(),
  {
    dockToBottom: false,
  },
);

defineSlots<{
  leading?(props: { option: TOption; index: number; isActive: boolean }): void;
  trailing?(props: { option: TOption; index: number; isActive: boolean }): void;
}>();

const listRef = useTemplateRef('listRef');

// Pointer activation must come from `mousemove`, not `mouseenter`: keyboard navigation scrolls this
// list, which slides a different option under a stationary cursor and makes the browser fire
// `mouseenter` for it — hijacking the active index mid-navigation.
const activateFromPointer = (index: number): void => {
  if (index === props.activeIndex) return;
  emit('update:activeIndex', index);
};

watch(
  () => props.activeIndex,
  async () => {
    await nextTick();
    const list = listRef.value;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (list && active) maintainScrollVisibility(active, list);
  },
);
</script>

<template>
  <div
    class="absolute left-0 z-40 w-[320px] max-w-full rounded-panel bg-surface-primary p-sm shadow-popover"
    :class="props.dockToBottom ? 'bottom-xs' : 'top-full mt-xs'"
  >
    <ul :id="props.listboxId" ref="listRef" role="listbox" class="max-h-[280px] overflow-y-auto">
      <li
        v-for="(option, index) in props.options"
        :id="getSuggestionOptionId(props.listboxId, index)"
        :key="option.id"
        role="option"
        :aria-selected="index === props.activeIndex"
        class="dropdown-option gap-sm text-md font-medium"
        :class="{ 'bg-surface-tertiary': index === props.activeIndex }"
        @mousedown.prevent="emit('select', option)"
        @mousemove="activateFromPointer(index)"
      >
        <slot name="leading" :option="option" :index="index" :is-active="index === props.activeIndex" />
        <span class="flex min-w-0 items-center gap-md">
          <span class="min-w-0 truncate">{{ option.label }}</span>
          <slot name="trailing" :option="option" :index="index" :is-active="index === props.activeIndex" />
        </span>
      </li>
    </ul>
  </div>
</template>
